import React, { useState } from "react";
import {
  CheckCircle,
  Send,
  XCircle,
  Phone,
  Clock,
  MessageCircle,
  Calendar,
  ArrowUpDown,
  Eye,
  Activity,
  Zap,
  Globe,
  X,
} from "lucide-react";
import { RootDispatch, RootState } from "../../../store";
import { useDispatch, useSelector } from "react-redux";
import { setSelectedDate } from "../../../store/slices/roundRobinSlice";

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const getStatusConfig = (status: string) => {
    switch (status) {
      case "DELIVERED":
        return { color: "bg-green-100 text-green-800", icon: CheckCircle };
      case "FAILED":
        return { color: "bg-red-100 text-red-800", icon: XCircle };
      case "SENT":
        return { color: "bg-blue-100 text-blue-800", icon: Send };
      case "READ":
        return { color: "bg-purple-100 text-purple-800", icon: Eye };
      default:
        return { color: "bg-gray-100 text-gray-800", icon: Clock };
    }
  };

  const { color, icon: Icon } = getStatusConfig(status);

  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${color}`}
    >
      <Icon className="h-3 w-3 mr-1" />
      {status}
    </span>
  );
};

export const ListData: React.FC = () => {
  const NumberWiseReport = useSelector(
    (state: RootState) => state.roundRobin.NumberWiseReport
  );
  const dispatch: RootDispatch = useDispatch();

  const selectedDate = useSelector(
    (state: RootState) => state.roundRobin.selectedDate
  );

  const [showDatePicker, setShowDatePicker] = useState(false);

  const getTodayDate = () => {
    return new Date().toISOString().split("T")[0];
  };

  const formatDisplayDate = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (dateString === today.toISOString().split("T")[0]) {
      return "Today";
    } else if (dateString === yesterday.toISOString().split("T")[0]) {
      return "Yesterday";
    } else {
      return date.toLocaleDateString("en-US", {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    }
  };

  return (
    <div className="overflow-hidden">
      {/* Enhanced Header with Filters */}
      <div className="px-8 py-6 border-b border-slate-200/50 bg-gradient-to-r from-slate-50/50 via-white/50 to-slate-50/50">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl shadow-lg">
                <MessageCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold bg-gradient-to-r from-slate-900 to-slate-600 bg-clip-text text-transparent">
                  Message Details for {formatDisplayDate(selectedDate)}
                </h2>
                <p className="text-sm text-slate-500 mt-1">
                  {NumberWiseReport?.length || 0} messages • Real-time tracking
                </p>
              </div>
            </div>
          </div>

          {/* Date Picker Controls */}
          <div className="flex items-center gap-3">
            <div className="relative">
              <button
                onClick={() => setShowDatePicker(!showDatePicker)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-all duration-200 shadow-sm"
              >
                <Calendar className="w-4 h-4 text-slate-600" />
                <span className="text-sm font-medium text-slate-700">
                  {formatDisplayDate(selectedDate)}
                </span>
                <ArrowUpDown className="w-3 h-3 text-slate-400" />
              </button>

              {showDatePicker && (
                <div className="absolute right-0 mt-2 p-4 bg-white border border-slate-200 rounded-xl shadow-lg z-50 min-w-[300px]">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold text-slate-900">
                      Select Date
                    </h3>
                    <button
                      onClick={() => setShowDatePicker(false)}
                      className="p-1 hover:bg-slate-100 rounded-lg transition-colors"
                    >
                      <X className="w-4 h-4 text-slate-500" />
                    </button>
                  </div>

                  {/* Date Input */}
                  <div className="space-y-3">
                    <input
                      max={getTodayDate()}
                      type="date"
                      value={selectedDate}
                      onChange={(e) => {
                        dispatch(setSelectedDate(e.target.value));
                        setShowDatePicker(false);
                      }}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    />

                    {/* Quick Date Options */}
                    <div className="grid grid-cols-1 gap-2">
                      <button
                        onClick={() => {
                          dispatch(setSelectedDate(getTodayDate()));
                          setShowDatePicker(false);
                        }}
                        className="px-3 py-2 text-sm bg-slate-50 hover:bg-slate-100 text-slate-700 rounded-lg transition-colors"
                      >
                        Today
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Table */}
      <div className="overflow-auto max-h-[600px]">
        <table className="w-full">
          <thead className="bg-gradient-to-r from-slate-50/70 via-white/50 to-slate-50/70 sticky top-0 z-10 backdrop-blur-xl">
            <tr>
              <th className="px-8 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                <button className="flex items-center gap-2 hover:text-slate-900 transition-colors duration-200 group">
                  <Phone className="h-4 w-4" />
                  Mobile Number
                  <ArrowUpDown className="h-3 w-3 group-hover:text-blue-500" />
                </button>
              </th>
              <th className="px-8 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                <button className="flex items-center gap-2 hover:text-slate-900 transition-colors duration-200 group">
                  <Activity className="h-4 w-4" />
                  Status
                  <ArrowUpDown className="h-3 w-3 group-hover:text-blue-500" />
                </button>
              </th>
              <th className="px-8 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                <button className="flex items-center gap-2 hover:text-slate-900 transition-colors duration-200 group">
                  <Calendar className="h-4 w-4" />
                  Sent At
                  <ArrowUpDown className="h-3 w-3 group-hover:text-blue-500" />
                </button>
              </th>
              <th className="px-8 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                <Zap className="h-4 w-4 inline mr-2" />
                Message ID
              </th>
            </tr>
          </thead>
          <tbody className="bg-white/30 divide-y divide-slate-200/50">
            {NumberWiseReport?.map((report, index) => (
              <tr
                key={index}
                className="hover:bg-white/60 transition-all duration-200 group"
              >
                <td className="px-8 py-5 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="ml-4">
                      <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                        {report.mobileNumber}
                        <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                      </div>
                      <div className="text-xs text-slate-500 flex items-center gap-1">
                        <Globe className="w-3 h-3" />
                        {report.mobileNumber.startsWith("91")
                          ? "India"
                          : "International"}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-8 py-5 whitespace-nowrap">
                  <StatusBadge status={report.status} />
                </td>
                <td className="px-8 py-5 whitespace-nowrap">
                  <div className="space-y-1">
                    <div className="text-sm font-medium text-slate-900">
                      {new Date(report.sentAt).toLocaleDateString()}
                    </div>
                    <div className="text-xs text-slate-500 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      <span>
                        {new Date(report.sentAt).toLocaleString("en-IN", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                        })}
                      </span>
                    </div>
                  </div>
                </td>
                <td className="px-8 py-5 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <div className="text-xs text-slate-500 font-mono bg-slate-100/70 backdrop-blur-sm px-3 py-1.5 rounded-lg border border-slate-200/50 max-w-xs truncate">
                      {report.messageId}
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Enhanced Footer */}
      <div className="px-8 py-6 border-t border-slate-200/50 bg-gradient-to-r from-slate-50/40 via-white/40 to-slate-50/40">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="text-sm text-slate-600">
              Showing{" "}
              <span className="font-semibold text-slate-900">
                {NumberWiseReport?.length || 0}
              </span>{" "}
              of{" "}
              <span className="font-semibold text-slate-900">
                {NumberWiseReport?.length || 0}
              </span>{" "}
              messages
            </div>
            {selectedDate !== getTodayDate() && (
              <div className="text-xs text-slate-500">
                • Showing data for {formatDisplayDate(selectedDate)}
              </div>
            )}
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
              <span>Live updates enabled</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
